import type { Game } from "./api";

function record(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}
function humanText(value: unknown): string {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value))
        return value.map(humanText).filter(Boolean).join(", ");
    const object = record(value);
    if (object)
        return Object.entries(object)
            .map(
                ([key, item]) =>
                    `${key.replace(/([a-z])([A-Z])/g, "$1 $2").replaceAll("_", " ")}: ${humanText(item)}`,
            )
            .join(" · ");
    return String(value);
}
function seatName(game: Game, id: string): string {
    const index = game.seats.findIndex((s) => s.id === id);
    const seat = game.seats[index];
    return seat
        ? `${seat.profile?.displayName ?? seat.name ?? "Player"} · Seat ${index + 1}`
        : id;
}
export function resultSummary(game: Game): string {
    const result = record(game.result);
    if (result && typeof result.reason === "string") return result.reason;
    if (result && Array.isArray(result.winners) && result.winners.length)
        return `Winner: ${result.winners.map((id) => seatName(game, String(id))).join(", ")}`;
    return humanText(game.result);
}
export function PublicResult({ game }: { game: Game }) {
    const result = record(game.result);
    const standings = Array.isArray(result?.standings)
        ? result.standings
              .map(record)
              .filter((row): row is Record<string, unknown> => row !== null)
        : [];
    const winners = Array.isArray(result?.winners)
        ? result.winners.map((id) => seatName(game, String(id)))
        : [];
    const extra = result
        ? Object.fromEntries(
              Object.entries(result).filter(
                  ([key]) => !["standings", "winners", "reason"].includes(key),
              ),
          )
        : null;
    return (
        <section className="guild-panel" aria-label="Completed game result">
            <h3>The tale is complete</h3>
            {game.completedAt && (
                <p>
                    Completed{" "}
                    <time dateTime={game.completedAt}>
                        {new Date(game.completedAt).toLocaleString()}
                    </time>
                </p>
            )}
            {!!winners.length && (
                <p>
                    <strong>
                        {winners.length === 1 ? "Winner" : "Winners"}:
                    </strong>{" "}
                    {winners.join(", ")}
                </p>
            )}
            {result && typeof result.reason === "string" && (
                <p>{result.reason}</p>
            )}
            {!!standings.length && (
                <ol className="guild-result-standings">
                    {standings.map((row, index) => (
                        <li key={`${row.seat ?? index}-${index}`}>
                            <strong>
                                {seatName(game, String(row.seat ?? "Player"))}
                            </strong>
                            <span>
                                {typeof row.rank === "number"
                                    ? `Rank ${row.rank} · `
                                    : ""}
                                {row.score !== undefined
                                    ? `Score: ${humanText(row.score)}`
                                    : ""}
                            </span>
                            {row.detail !== undefined && (
                                <small>{humanText(row.detail)}</small>
                            )}
                        </li>
                    ))}
                </ol>
            )}
            {extra && Object.keys(extra).length > 0 && (
                <p>{humanText(extra)}</p>
            )}
            {!result && game.result != null && <p>{humanText(game.result)}</p>}
        </section>
    );
}
